/**
 * Wave 29: 10 new cities
 * Rennes 🇫🇷, Düsseldorf 🇩🇪, Brno 🇨🇿, Porto 🇵🇹, Gothenburg 🇸🇪,
 * Lucknow 🇮🇳, Busan 🇰🇷, Accra Hub2 🇬🇭, Manama Hub2 🇧🇭, Luanda 🇦🇴
 */
import type { CityAgencyData } from './city-agency-pages';

export const CITY_AGENCY_PAGES_WAVE29: Record<string, CityAgencyData> = {
  rennes: {
    slug: 'software-development-agency-rennes',
    cityName: 'Rennes',
    countryName: 'France',
    flag: '🇫🇷',
    timezone: 'CET (UTC+1)',
    currency: 'EUR',
    title: { en: 'Software Development Agency Rennes | Call IT Dev', fr: 'Agence Développement Web Rennes | Call IT Dev' },
    description: { en: 'Top software development agency in Rennes. Cybersecurity, SaaS, mobile & AI. From $25/hr vs €550/day local.', fr: 'Agence développement logiciel Rennes. Cybersécurité, SaaS, mobile & IA. Dès 25$/h vs 550€/jour local.' },
    keywords: ['software development Rennes', 'agence développement Rennes', 'développeur Rennes'],
    heroTitle: { en: 'Software Development Agency in Rennes', fr: 'Agence de Développement Logiciel à Rennes' },
    heroSubtitle: { en: 'Brittany\'s tech capital meets nearshore excellence. 65% savings vs local Rennes agencies.', fr: 'La capitale tech bretonne rencontre l\'excellence nearshore. 65% d\'économies.' },
    whyTitle: { en: 'Why Choose Us Over Rennes Agencies', fr: 'Pourquoi Nous Choisir vs Agences Rennaises' },
    whyReasons: [
      { icon: '🔒', title: 'Cybersecurity Hub', description: 'Expertise aligned with Rennes\' cybersecurity cluster.' },
      { icon: '💰', title: '65% Savings', description: 'Senior developers from $25/hr vs €500-600/day in Rennes.' },
      { icon: '🕐', title: 'Same Timezone', description: 'CET-aligned for real-time collaboration.' },
      { icon: '🇫🇷', title: 'French Native', description: 'Native French developers and project managers.' },
    ],
    services: [
      { name: 'Cybersecurity', description: 'Pen testing, SOC, SIEM and security consulting.' },
      { name: 'SaaS Development', description: 'Multi-tenant SaaS with French hosting options.' },
      { name: 'Mobile Apps', description: 'Cross-platform mobile with Flutter and React Native.' },
      { name: 'AI & Data', description: 'Machine learning, NLP and data analytics pipelines.' },
    ],
    industries: [
      { name: 'Defense & Cyber', description: 'Solutions for Rennes\' defense and cybersecurity sector.' },
      { name: 'AgriTech', description: 'Digital solutions for Brittany\'s agriculture industry.' },
      { name: 'Telecom', description: 'Telecom infrastructure and 5G applications.' },
    ],
    faqs: [
      { question: 'Do you have cybersecurity expertise?', answer: 'Yes, we have certified security engineers aligned with Rennes\' Pôle d\'Excellence Cyber standards.' },
      { question: 'How fast can you start?', answer: 'We can kick off a project within 5-7 business days with a dedicated team.' },
    ],
    localRate: '€500-600/day',
    ourRate: '$25-45/hr',
    savings: '60-70%',
  },

  dusseldorf: {
    slug: 'software-development-agency-dusseldorf',
    cityName: 'Düsseldorf',
    countryName: 'Germany',
    flag: '🇩🇪',
    timezone: 'CET (UTC+1)',
    currency: 'EUR',
    title: { en: 'Software Development Agency Düsseldorf | Call IT Dev', de: 'Softwareentwicklung Düsseldorf | Call IT Dev' },
    description: { en: 'Software development agency Düsseldorf. Enterprise portals, e-commerce, AI & cloud. From $25/hr vs €650/day local.', de: 'Softwareentwicklung Düsseldorf. Enterprise-Portale, E-Commerce, KI & Cloud. Ab 25$/h vs 650€/Tag.' },
    keywords: ['software development Düsseldorf', 'Softwareentwicklung Düsseldorf', 'IT Düsseldorf'],
    heroTitle: { en: 'Software Development Agency in Düsseldorf', de: 'Softwareentwicklungsagentur in Düsseldorf' },
    heroSubtitle: { en: 'Rhine-Ruhr enterprise software at nearshore prices. 70% savings vs Düsseldorf rates.', de: 'Enterprise-Software für Rhein-Ruhr zu Nearshore-Preisen. 70% Ersparnis.' },
    whyTitle: { en: 'Why Choose Us Over Düsseldorf Agencies', de: 'Warum uns statt Düsseldorfer Agenturen' },
    whyReasons: [
      { icon: '🏢', title: 'Enterprise Focus', description: 'B2B enterprise software for NRW\'s business ecosystem.' },
      { icon: '💰', title: '70% Savings', description: 'Senior developers from $25/hr vs €600-700/day.' },
      { icon: '🕐', title: 'CET Aligned', description: 'Same timezone as your Düsseldorf headquarters.' },
      { icon: '🛒', title: 'E-Commerce', description: 'Shopify Plus, Magento & custom e-commerce expertise.' },
    ],
    services: [
      { name: 'Enterprise Portals', description: 'Customer and partner portals with SSO.' },
      { name: 'E-Commerce', description: 'B2B and B2C e-commerce platforms.' },
      { name: 'Cloud Migration', description: 'AWS, Azure and multi-cloud strategies.' },
      { name: 'AI Integration', description: 'AI-powered automation and analytics.' },
    ],
    industries: [
      { name: 'Retail & Fashion', description: 'E-commerce for Düsseldorf\'s fashion industry.' },
      { name: 'Consulting', description: 'Digital tools for management consultancies.' },
      { name: 'Telecom', description: 'Vodafone and telco ecosystem solutions.' },
    ],
    faqs: [
      { question: 'Can you work with German enterprises?', answer: 'Yes, we support DAX and Mittelstand companies with GDPR-compliant solutions.' },
      { question: 'Do you speak German?', answer: 'We have German-speaking project managers for seamless communication.' },
    ],
    localRate: '€600-700/day',
    ourRate: '$25-45/hr',
    savings: '65-75%',
  },

  brno: {
    slug: 'software-development-agency-brno',
    cityName: 'Brno',
    countryName: 'Czech Republic',
    flag: '🇨🇿',
    timezone: 'CET (UTC+1)',
    currency: 'CZK',
    title: { en: 'Software Development Agency Brno | Call IT Dev' },
    description: { en: 'Software development agency Brno, Czech Republic. Full-stack, SaaS, mobile & AI. From $25/hr vs CZK 8,000/day.' },
    keywords: ['software development Brno', 'IT company Brno', 'developers Czech Republic'],
    heroTitle: { en: 'Software Development Agency in Brno' },
    heroSubtitle: { en: 'Central Europe\'s hidden tech gem. 55% savings vs local Brno agencies.' },
    whyTitle: { en: 'Why Choose Us Over Brno Agencies' },
    whyReasons: [
      { icon: '🎓', title: 'University Talent', description: 'Leveraging Brno\'s strong CS university ecosystem.' },
      { icon: '💰', title: '55% Savings', description: 'Competitive rates even vs Czech nearshore prices.' },
      { icon: '🕐', title: 'CET Timezone', description: 'Same working hours as Prague and Vienna.' },
      { icon: '🇪🇺', title: 'EU Quality', description: 'EU-standard development with GDPR compliance.' },
    ],
    services: [
      { name: 'Full-Stack', description: 'React, Angular, Node.js and .NET solutions.' },
      { name: 'DevOps', description: 'CI/CD, Kubernetes and infrastructure automation.' },
      { name: 'Mobile Apps', description: 'Native and cross-platform mobile development.' },
      { name: 'QA & Testing', description: 'Manual and automated testing services.' },
    ],
    industries: [
      { name: 'Gaming', description: 'Game development and gaming platform backends.' },
      { name: 'Automotive', description: 'Connected car and ADAS software solutions.' },
      { name: 'Biotech', description: 'Lab management and biotech data platforms.' },
    ],
    faqs: [
      { question: 'Why Brno over Prague?', answer: 'We offer Brno-competitive quality at even lower rates with a larger talent pool.' },
      { question: 'Do you support startups?', answer: 'Yes, we\'ve helped 60+ startups go from idea to MVP in 4-8 weeks.' },
    ],
    localRate: 'CZK 7,000-9,000/day',
    ourRate: '$25-40/hr',
    savings: '50-60%',
  },

  porto: {
    slug: 'software-development-agency-porto',
    cityName: 'Porto',
    countryName: 'Portugal',
    flag: '🇵🇹',
    timezone: 'WET (UTC+0)',
    currency: 'EUR',
    title: { en: 'Software Development Agency Porto | Call IT Dev', pt: 'Agência Desenvolvimento Software Porto | Call IT Dev' },
    description: { en: 'Software development agency Porto, Portugal. SaaS, fintech, mobile & AI. From $25/hr vs €450/day local.', pt: 'Agência desenvolvimento software Porto. SaaS, fintech, mobile & IA. Desde 25$/h vs 450€/dia.' },
    keywords: ['software development Porto', 'agência desenvolvimento Porto', 'IT Porto Portugal'],
    heroTitle: { en: 'Software Development Agency in Porto', pt: 'Agência de Desenvolvimento de Software no Porto' },
    heroSubtitle: { en: 'Portugal\'s tech hub meets global nearshore. 55% savings vs Porto rates.', pt: 'O hub tecnológico de Portugal com nearshore global. 55% de poupança.' },
    whyTitle: { en: 'Why Choose Us Over Porto Agencies', pt: 'Porquê Escolher-nos vs Agências do Porto' },
    whyReasons: [
      { icon: '🌊', title: 'Atlantic Gateway', description: 'Perfect timezone overlap with UK and US East Coast.' },
      { icon: '💰', title: '55% Savings', description: 'Senior developers from $25/hr vs €400-500/day.' },
      { icon: '🇵🇹', title: 'Portuguese Native', description: 'Native Portuguese developers for PT/BR markets.' },
      { icon: '🏦', title: 'FinTech Ready', description: 'PSD2, Open Banking and payment platform expertise.' },
    ],
    services: [
      { name: 'FinTech', description: 'Payment gateways, neobanking and lending platforms.' },
      { name: 'SaaS', description: 'Multi-tenant SaaS with EU data residency.' },
      { name: 'Mobile Apps', description: 'Consumer and enterprise mobile applications.' },
      { name: 'AI & ML', description: 'Predictive analytics and recommendation engines.' },
    ],
    industries: [
      { name: 'Tourism', description: 'Travel tech and hospitality platforms.' },
      { name: 'Wine & Food', description: 'AgriTech and supply chain for wine industry.' },
      { name: 'Real Estate', description: 'PropTech and property management software.' },
    ],
    faqs: [
      { question: 'Do you serve Portuguese-speaking markets?', answer: 'Yes, we support both Portugal and Brazil markets with native Portuguese teams.' },
      { question: 'What about data residency?', answer: 'We offer EU-hosted solutions compliant with GDPR and Portuguese CNPD requirements.' },
    ],
    localRate: '€400-500/day',
    ourRate: '$25-40/hr',
    savings: '50-60%',
  },

  gothenburg: {
    slug: 'software-development-agency-gothenburg',
    cityName: 'Gothenburg',
    countryName: 'Sweden',
    flag: '🇸🇪',
    timezone: 'CET (UTC+1)',
    currency: 'SEK',
    title: { en: 'Software Development Agency Gothenburg | Call IT Dev', sv: 'Mjukvaruutveckling Göteborg | Call IT Dev' },
    description: { en: 'Software development agency Gothenburg. Automotive, IoT, SaaS & AI. From $25/hr vs SEK 8,000/day.', sv: 'Mjukvaruutveckling Göteborg. Automotive, IoT, SaaS & AI. Från $25/h vs 8 000 SEK/dag.' },
    keywords: ['software development Gothenburg', 'mjukvaruutveckling Göteborg', 'IT Gothenburg'],
    heroTitle: { en: 'Software Development Agency in Gothenburg', sv: 'Mjukvaruutvecklingsföretag i Göteborg' },
    heroSubtitle: { en: 'Volvo-grade engineering at nearshore prices. 65% savings vs Gothenburg rates.', sv: 'Volvo-kvalitet till nearshore-priser. 65% besparing vs Göteborg.' },
    whyTitle: { en: 'Why Choose Us Over Gothenburg Agencies', sv: 'Varför Välja Oss Istället för Göteborgsbyråer' },
    whyReasons: [
      { icon: '🚗', title: 'Automotive DNA', description: 'Software for Volvo, Geely and automotive ecosystem.' },
      { icon: '💰', title: '65% Savings', description: 'Senior developers from $25/hr vs SEK 7,000-9,000/day.' },
      { icon: '🕐', title: 'CET Aligned', description: 'Same timezone for seamless collaboration.' },
      { icon: '🏭', title: 'IoT Expert', description: 'Connected devices and smart manufacturing.' },
    ],
    services: [
      { name: 'Automotive Software', description: 'ADAS, fleet management and connected car platforms.' },
      { name: 'IoT Development', description: 'Industrial IoT and smart factory solutions.' },
      { name: 'SaaS', description: 'B2B SaaS platforms for Nordic markets.' },
      { name: 'AI & ML', description: 'Predictive maintenance and computer vision.' },
    ],
    industries: [
      { name: 'Automotive', description: 'Software for Gothenburg\'s automotive cluster.' },
      { name: 'Shipping', description: 'Maritime logistics and port management systems.' },
      { name: 'MedTech', description: 'Medical device software and health platforms.' },
    ],
    faqs: [
      { question: 'Do you work with automotive companies?', answer: 'Yes, we support automotive OEMs and suppliers with AUTOSAR and connected vehicle solutions.' },
      { question: 'Can you handle Swedish language?', answer: 'Yes, we have Swedish-speaking PMs and full Nordic localization capability.' },
    ],
    localRate: 'SEK 7,000-9,000/day',
    ourRate: '$25-45/hr',
    savings: '60-70%',
  },

  lucknow: {
    slug: 'software-development-agency-lucknow',
    cityName: 'Lucknow',
    countryName: 'India',
    flag: '🇮🇳',
    timezone: 'IST (UTC+5:30)',
    currency: 'INR',
    title: { en: 'Software Development Agency Lucknow | Call IT Dev' },
    description: { en: 'Software development agency Lucknow. Full-stack, mobile, cloud & AI. From $15/hr. Emerging UP tech hub.' },
    keywords: ['software development Lucknow', 'IT company Lucknow', 'app development Lucknow'],
    heroTitle: { en: 'Software Development Agency in Lucknow' },
    heroSubtitle: { en: 'Uttar Pradesh\'s emerging tech hub. Enterprise-grade software at global-best rates.' },
    whyTitle: { en: 'Why Choose Us for Lucknow Projects' },
    whyReasons: [
      { icon: '🌆', title: 'Smart City', description: 'Software for Lucknow\'s smart city initiatives.' },
      { icon: '💰', title: 'Best Rates', description: 'Senior developers from $15/hr with UP market expertise.' },
      { icon: '🌏', title: 'Global Reach', description: '16+ language support for worldwide clients.' },
      { icon: '🏛️', title: 'Gov Tech', description: 'E-governance platforms for state and central government.' },
    ],
    services: [
      { name: 'Gov Tech', description: 'E-governance, citizen portals and public services.' },
      { name: 'Mobile Apps', description: 'Android-first apps for Indian market.' },
      { name: 'E-commerce', description: 'D2C platforms and marketplace solutions.' },
      { name: 'Cloud', description: 'AWS and Azure cloud migration and management.' },
    ],
    industries: [
      { name: 'Government', description: 'Digital government and public administration.' },
      { name: 'Education', description: 'EdTech for UP\'s massive education sector.' },
      { name: 'Healthcare', description: 'Telemedicine and hospital management systems.' },
    ],
    faqs: [
      { question: 'Do you serve UP businesses?', answer: 'Yes, we support businesses across Uttar Pradesh with locally-adapted solutions.' },
      { question: 'What about government projects?', answer: 'We have experience with e-governance and smart city initiatives.' },
    ],
    localRate: '₹3,500-5,000/day',
    ourRate: '$15-30/hr',
    savings: '40-50%',
  },

  busan: {
    slug: 'software-development-agency-busan',
    cityName: 'Busan',
    countryName: 'South Korea',
    flag: '🇰🇷',
    timezone: 'KST (UTC+9)',
    currency: 'KRW',
    title: { en: 'Software Development Agency Busan | Call IT Dev', ko: '소프트웨어 개발 부산 | Call IT Dev' },
    description: { en: 'Software development agency Busan. Blockchain, fintech, mobile & AI. From $25/hr vs ₩800,000/day.', ko: '부산 소프트웨어 개발 에이전시. 블록체인, 핀테크, 모바일 & AI. $25/시간부터.' },
    keywords: ['software development Busan', '소프트웨어 개발 부산', 'IT outsourcing Busan'],
    heroTitle: { en: 'Software Development Agency in Busan', ko: '부산 소프트웨어 개발 에이전시' },
    heroSubtitle: { en: 'Korea\'s second city meets global nearshore. 60% savings vs Busan rates.', ko: '대한민국 제2의 도시와 글로벌 니어쇼어의 만남. 60% 절감.' },
    whyTitle: { en: 'Why Choose Us Over Busan Agencies', ko: '왜 부산 에이전시 대신 저희를 선택해야 하는가' },
    whyReasons: [
      { icon: '⛓️', title: 'Blockchain Hub', description: 'Smart contracts and DeFi for Korea\'s blockchain capital.' },
      { icon: '💰', title: '60% Savings', description: 'Senior developers from $25/hr vs ₩700K-900K/day.' },
      { icon: '🇰🇷', title: 'Korea Ready', description: 'Korean-speaking PMs with local market knowledge.' },
      { icon: '🚢', title: 'Maritime Tech', description: 'Shipping and logistics solutions for Asia\'s port city.' },
    ],
    services: [
      { name: 'Blockchain', description: 'Smart contracts, NFTs and DeFi platforms.' },
      { name: 'FinTech', description: 'Payment processing and digital banking.' },
      { name: 'Mobile Apps', description: 'iOS/Android apps for the Korean market.' },
      { name: 'AI & Data', description: 'ML models and data analytics.' },
    ],
    industries: [
      { name: 'Shipping', description: 'Maritime logistics and port management.' },
      { name: 'Film & Media', description: 'BIFF-adjacent media tech solutions.' },
      { name: 'Tourism', description: 'Travel platforms for coastal tourism.' },
    ],
    faqs: [
      { question: 'Do you support Korean projects?', answer: 'Yes, with Korean-speaking PMs and full localization for the Korean market.' },
      { question: 'Blockchain expertise?', answer: 'We\'ve delivered 30+ blockchain projects including DeFi, NFTs and tokenization.' },
    ],
    localRate: '₩700,000-900,000/day',
    ourRate: '$25-45/hr',
    savings: '55-65%',
  },

  accraHub2: {
    slug: 'software-development-agency-accra-hub2',
    cityName: 'Accra Hub 2',
    countryName: 'Ghana',
    flag: '🇬🇭',
    timezone: 'GMT (UTC+0)',
    currency: 'GHS',
    title: { en: 'Software Development Agency Accra | Call IT Dev' },
    description: { en: 'Software development agency Accra, Ghana. FinTech, mobile, SaaS & AI. From $20/hr. West Africa\'s tech capital.' },
    keywords: ['software development Accra', 'IT company Ghana', 'developers Accra'],
    heroTitle: { en: 'Software Development Agency in Accra' },
    heroSubtitle: { en: 'West Africa\'s Silicon Valley. Enterprise-grade software for the African continent.' },
    whyTitle: { en: 'Why Choose Us for Accra Projects' },
    whyReasons: [
      { icon: '🌍', title: 'Africa Hub', description: 'Gateway to West African digital markets.' },
      { icon: '💰', title: 'Best Value', description: 'Senior developers from $20/hr for African projects.' },
      { icon: '🏦', title: 'FinTech Focus', description: 'Mobile money and digital banking expertise.' },
      { icon: '🕐', title: 'GMT Aligned', description: 'Perfect timezone for UK and EU collaboration.' },
    ],
    services: [
      { name: 'FinTech', description: 'Mobile money, digital wallets and lending platforms.' },
      { name: 'Mobile Apps', description: 'Android-first apps for African markets.' },
      { name: 'SaaS', description: 'Cloud-native SaaS platforms.' },
      { name: 'AgriTech', description: 'Precision agriculture and supply chain.' },
    ],
    industries: [
      { name: 'Banking', description: 'Digital banking for West Africa.' },
      { name: 'Telecom', description: 'Mobile operator value-added services.' },
      { name: 'Agriculture', description: 'Farm management and commodity trading platforms.' },
    ],
    faqs: [
      { question: 'Do you serve West African markets?', answer: 'Yes, we support businesses across Ghana, Nigeria and the ECOWAS region.' },
      { question: 'Mobile money integration?', answer: 'We integrate with MTN MoMo, Vodafone Cash, AirtelTigo and all major mobile money providers.' },
    ],
    localRate: 'GHS 2,000-3,500/day',
    ourRate: '$20-35/hr',
    savings: '45-55%',
  },

  manamaHub2: {
    slug: 'software-development-agency-manama-hub3',
    cityName: 'Manama Hub 3',
    countryName: 'Bahrain',
    flag: '🇧🇭',
    timezone: 'AST (UTC+3)',
    currency: 'BHD',
    title: { en: 'Software Development Agency Manama | Call IT Dev', ar: 'شركة تطوير برمجيات المنامة | Call IT Dev' },
    description: { en: 'Software development agency Manama, Bahrain. FinTech, Islamic banking, mobile & AI. From $25/hr.', ar: 'شركة تطوير برمجيات المنامة، البحرين. فينتك، مصرفية إسلامية. من 25$/ساعة.' },
    keywords: ['software development Manama', 'IT company Bahrain', 'تطوير برمجيات المنامة'],
    heroTitle: { en: 'Software Development Agency in Manama', ar: 'شركة تطوير برمجيات في المنامة' },
    heroSubtitle: { en: 'Bahrain\'s FinTech hub meets nearshore excellence. 60% savings vs local rates.', ar: 'مركز البحرين للتكنولوجيا المالية يلتقي بالتميز النيرشور.' },
    whyTitle: { en: 'Why Choose Us for Bahrain Projects', ar: 'لماذا تختارنا لمشاريع البحرين' },
    whyReasons: [
      { icon: '🏦', title: 'Islamic FinTech', description: 'Sharia-compliant fintech and banking platforms.' },
      { icon: '💰', title: '60% Savings', description: 'Senior developers from $25/hr vs BHD 250/day.' },
      { icon: '🌐', title: 'Arabic Native', description: 'Arabic-first interfaces with RTL design.' },
      { icon: '🔒', title: 'CBB Compliant', description: 'Central Bank of Bahrain regulatory compliance.' },
    ],
    services: [
      { name: 'Islamic FinTech', description: 'Sharia-compliant banking and investment platforms.' },
      { name: 'E-Government', description: 'Smart government and digital services.' },
      { name: 'Mobile Apps', description: 'Arabic-first mobile applications.' },
      { name: 'AI & Data', description: 'AI-powered analytics and automation.' },
    ],
    industries: [
      { name: 'Banking', description: 'Islamic and conventional banking software.' },
      { name: 'Insurance', description: 'Takaful and conventional insurance platforms.' },
      { name: 'Oil & Gas', description: 'Energy sector digitalization.' },
    ],
    faqs: [
      { question: 'Do you build Sharia-compliant solutions?', answer: 'Yes, we specialize in Islamic FinTech with Sharia-compliant architecture.' },
      { question: 'CBB compliance?', answer: 'All our financial solutions follow CBB regulatory requirements.' },
    ],
    localRate: 'BHD 200-300/day',
    ourRate: '$25-45/hr',
    savings: '55-65%',
  },

  luanda: {
    slug: 'software-development-agency-luanda',
    cityName: 'Luanda',
    countryName: 'Angola',
    flag: '🇦🇴',
    timezone: 'WAT (UTC+1)',
    currency: 'AOA',
    title: { en: 'Software Development Agency Luanda | Call IT Dev', pt: 'Agência Desenvolvimento Software Luanda | Call IT Dev' },
    description: { en: 'Software development agency Luanda, Angola. Oil & gas tech, mobile, fintech & AI. From $20/hr.', pt: 'Agência desenvolvimento software Luanda, Angola. Petróleo & gás, mobile, fintech & IA. Desde 20$/h.' },
    keywords: ['software development Luanda', 'IT company Angola', 'agência desenvolvimento Luanda'],
    heroTitle: { en: 'Software Development Agency in Luanda', pt: 'Agência de Desenvolvimento de Software em Luanda' },
    heroSubtitle: { en: 'Angola\'s digital transformation partner. Enterprise software for oil & gas and beyond.', pt: 'Parceiro de transformação digital de Angola. Software empresarial para petróleo & gás.' },
    whyTitle: { en: 'Why Choose Us for Angola Projects', pt: 'Porquê Escolher-nos para Projetos em Angola' },
    whyReasons: [
      { icon: '🛢️', title: 'Oil & Gas', description: 'Digital solutions for Angola\'s energy sector.' },
      { icon: '💰', title: 'Best Value', description: 'Senior developers from $20/hr for African projects.' },
      { icon: '🇵🇹', title: 'Portuguese Native', description: 'Native Portuguese developers for Lusophone Africa.' },
      { icon: '📱', title: 'Mobile First', description: 'Mobile-centric solutions for Angola\'s digital economy.' },
    ],
    services: [
      { name: 'Energy Tech', description: 'Oil & gas operations management and IoT.' },
      { name: 'FinTech', description: 'Mobile payments and digital banking for Angola.' },
      { name: 'Mobile Apps', description: 'Android-first apps for Angolan market.' },
      { name: 'E-Government', description: 'Government digitalization and citizen portals.' },
    ],
    industries: [
      { name: 'Oil & Gas', description: 'Sonangol ecosystem and energy tech.' },
      { name: 'Banking', description: 'Digital banking and mobile money solutions.' },
      { name: 'Telecom', description: 'Unitel and telecom VAS platforms.' },
    ],
    faqs: [
      { question: 'Do you serve Angolan businesses?', answer: 'Yes, we support businesses across Angola with Portuguese-speaking teams.' },
      { question: 'Oil & gas expertise?', answer: 'We build IoT monitoring, HSE management and operations platforms for the energy sector.' },
    ],
    localRate: 'AOA 150,000-250,000/day',
    ourRate: '$20-35/hr',
    savings: '50-60%',
  },
};
